import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Pill, LogOut, Plus, FileText, Clock, CheckCircle, CheckCircle2, XCircle, Package, ArrowRight,
  User, CreditCard, Upload, RefreshCw, Phone, Mail, ChevronDown, ChevronRight, Pause, Play, Ban,
  Eye, Loader2, Receipt, Download, ShieldAlert, Utensils, Dumbbell, Activity, ScanLine, Gift
} from "lucide-react";
import { toast } from "sonner";
import PatientHelpTooltip from "@/components/PatientHelpTooltip";
import PatientHelpTour from "@/components/PatientHelpTour";
import { HIPAAPatientBanner } from "@/components/HIPAACompliance";
import PastDueBanner from "@/components/patient/PastDueBanner";
import ReferralCard from "@/components/ReferralCard";
import SavingsWidget from "@/components/SavingsWidget";
import SubscriptionsCard from "@/components/SubscriptionsCard";
import DeliveryExceptionCard from "@/components/DeliveryExceptionCard";
import BillingPreviewCard from "@/components/BillingPreviewCard";
import BenefitsShareModal, { openBenefitsShareModal } from "@/components/BenefitsShareModal";
import PublicFooter from "@/components/PublicFooter";
import ReceiptPreviewDialog from "@/components/ReceiptPreviewDialog";
import SymptomStreakBadge from "@/components/SymptomStreakBadge";
import WellnessCard from "@/components/WellnessCard";
import PatientSymptomCard from "@/components/PatientSymptomCard";
import IdVerificationCard from "@/components/IdVerificationCard";
import COASearchCard from "@/components/COASearchCard";
import IntuitCheckoutModal from "@/components/IntuitCheckoutModal";
import { intuitCtxFromCheckoutError } from "@/lib/intuitCheckout";
import AccessGate from "@/components/AccessGate";
import DeliveryIncludesCard from "@/components/DeliveryIncludesCard";
import YouTubeLibraryCard from "@/components/YouTubeLibraryCard";
import WellnessCoachCard from "@/components/WellnessCoachCard";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";
import LanguageToggle from "@/components/LanguageToggle";

const STATUS_MAP = {
  pending_payment: { label: "Awaiting Payment", color: "bg-blue-50 text-blue-700 border-blue-200", icon: CreditCard },
  pending_review: { label: "Pending Review", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle },
  shipped: { label: "Shipped", color: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: Package },
  delivered: { label: "Delivered", color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle },
  denied: { label: "Denied", color: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-500 border-slate-200", icon: XCircle },
};

// Tracker-pill state lookup — flat lookup beats a nested ternary for readability.
function dotClass(isComplete, isCurrent) {
  if (isComplete) return "bg-emerald-500";
  if (isCurrent) return "bg-purple-500 animate-pulse";
  return "bg-slate-200";
}

export default function PatientDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [receiptsByOrder, setReceiptsByOrder] = useState({});
  const [previewReceipt, setPreviewReceipt] = useState(null); // {session_id, receipt_id, tier_label}
  const [loading, setLoading] = useState(true);
  const [contactInfo, setContactInfo] = useState({ phone_number: "", secondary_phone_number: "", email_address: "" });
  const [onboarding, setOnboarding] = useState(null);
  const [idStatus, setIdStatus] = useState(null);  // for the smart "Order Medication" CTA
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refillModal, setRefillModal] = useState(null);
  const [refillLoading, setRefillLoading] = useState(false);
  const [refillNote, setRefillNote] = useState("");
  const [subAction, setSubAction] = useState(null);
  const [pharmacyMap, setPharmacyMap] = useState({});      // {id: name}
  const [defaultPharmacy, setDefaultPharmacy] = useState(""); // first active LabFlo pharmacy
  const [access, setAccess] = useState(null); // {has_access, reason, ...}
  const [questionnaireDone, setQuestionnaireDone] = useState(false);
  const [topBadge, setTopBadge] = useState(null);
  const [streakBell, setStreakBell] = useState(null);
  const [intuitCtx, setIntuitCtx] = useState(null);
  const [restoreOffer, setRestoreOffer] = useState(null);

  useEffect(() => {
    // Highest lifetime step-milestone badge — shown as a chip next to the
    // welcome greeting. Falls back to null silently for first-time patients.
    API.get("/stepathon/top-badge")
      .then((r) => setTopBadge(r.data?.top_badge || null))
      .catch(() => setTopBadge(null));
    // Streak-save bell — lit when today's streak-save push has fired.
    API.get("/wellness/streak-save-status")
      .then((r) => setStreakBell(r.data || null))
      .catch(() => setStreakBell(null));
    // Streak-loss softening — offer to restore within 48h of a break.
    API.get("/wellness/streak-restore-offer")
      .then((r) => setRestoreOffer(r.data || null))
      .catch(() => setRestoreOffer(null));
  }, []);

  const doStreakRestore = async () => {
    try {
      const r = await API.post("/wellness/streak-restore");
      toast.success(`🔥 Streak restored to day ${r.data?.daily_streak || ""}!`);
      setRestoreOffer({ offer_available: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't restore streak.");
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, contactRes, onbRes, receiptsRes, pharmRes, accessRes, qRes] = await Promise.all([
        API.get("/orders/mine"),
        API.get("/contact-info").catch(() => ({ data: { phone_number: "", secondary_phone_number: "", email_address: "" } })),
        API.get("/onboarding/status").catch(() => ({ data: null })),
        API.get("/receipts/mine").catch(() => ({ data: [] })),
        API.get("/pharmacy-tenants").catch(() => ({ data: [] })),
        API.get("/wellness/access").catch(() => ({ data: { has_access: false, reason: "unknown" } })),
        API.get("/questionnaires/mine").catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data);
      setContactInfo(contactRes.data);
      setOnboarding(onbRes.data);
      // Detect a completed questionnaire so the "Questionnaire" quick-
      // action tile can render greyed-out (locked to one submission).
      // Backend `questionnaires` collection stores every submission; we
      // treat status="completed" OR any submitted_at timestamp as done.
      const qList = Array.isArray(qRes.data) ? qRes.data : [];
      setQuestionnaireDone(qList.some((q) => q.status === "completed" || !!q.submitted_at));
      // Build order_id → most-recent-receipt map. /api/receipts/mine returns
      // descending by paid_at, so the first hit per order_id wins.
      const map = {};
      for (const r of (receiptsRes.data || [])) {
        if (r.order_id && !map[r.order_id]) {
          map[r.order_id] = r;
        }
      }
      setReceiptsByOrder(map);
      setAccess(accessRes.data);
      // Build pharmacy id → name map for the trust badge
      const pmap = {};
      let firstActive = "";
      for (const p of (pharmRes.data || [])) {
        if (p.id && p.name) pmap[p.id] = p.name;
        if (!firstActive && p.status === "active" && p.name) firstActive = p.name;
      }
      setPharmacyMap(pmap);
      setDefaultPharmacy(firstActive);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Identity-proofing status drives whether the "Order Medication" CTA
  // shows the normal "Browse & order" copy or an amber "Verify ID & Order"
  // call-out. The same /medications gate enforces the requirement after
  // they click — this just makes the missing step obvious one screen earlier.
  useEffect(() => {
    let alive = true;
    API.get("/me/id-verification/status")
      .then(({ data }) => { if (alive) setIdStatus(data); })
      .catch(() => { /* non-patient roles or transient — fall back to default copy */ });
    return () => { alive = false; };
  }, []);

  const idGateBlocking = !!(idStatus && idStatus.verification_required && !idStatus.verified);

  const openReceiptPreview = useCallback((orderId) => {
    const r = receiptsByOrder[orderId];
    if (!r) {
      toast.info("No receipt is available for this order yet.");
      return;
    }
    setPreviewReceipt(r);
  }, [receiptsByOrder]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const viewOrderDetails = async (order) => {
    setSelectedOrder(order);
    setDetailLoading(true);
    try {
      const { data } = await API.get(`/orders/${order.id}/details`);
      setOrderDetail(data);
    } catch { setOrderDetail(null); } finally { setDetailLoading(false); }
  };

  const handleRefill = async (order) => {
    setRefillLoading(true);
    let newOrder;
    try {
      const { data } = await API.post("/orders/refill", {
        original_order_id: order.id,
        payment_type: order.payment_type || "one_time",
        patient_note: refillNote.trim() || null,
      });
      newOrder = data;
      // Go to payment
      const endpoint = newOrder.payment_type === "subscription" ? "/payments/subscription-checkout" : "/payments/checkout";
      const { executeCaptcha } = await import("@/lib/recaptcha");
      const captcha_token = await executeCaptcha("checkout").catch(() => null);
      const { data: checkout } = await API.post(endpoint, {
        medication_id: newOrder.medication_id,
        order_id: newOrder.id,
        origin_url: window.location.origin,
        payment_type: newOrder.payment_type,
        captcha_token,
        captcha_version: "v3",
      });
      if (checkout.url) {
        window.location.href = checkout.url;
      }
    } catch (e) {
      const intuitBranch = intuitCtxFromCheckoutError(e, {
        medication_id: newOrder?.medication_id,
        payment_type: newOrder?.payment_type,
        order_id: newOrder?.id,
        amount: newOrder?.amount,
      });
      if (intuitBranch) { setIntuitCtx(intuitBranch); return; }
      toast.error(e.response?.data?.detail || "Failed to create refill");
    } finally { setRefillLoading(false); setRefillModal(null); setRefillNote(""); }
  };

  const handleSubAction = async (orderId, action) => {
    try {
      await API.put(`/orders/${orderId}/subscription`, { action, order_id: orderId });
      toast.success(`Subscription ${action}d`);
      loadData();
      setSubAction(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleCancelOrder = async (orderId) => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "Are you sure you want to cancel this order?", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'info' }))) return;
    try {
      await API.put(`/orders/${orderId}/cancel`);
      toast.success("Order cancelled successfully");
      loadData();
      setSelectedOrder(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel order"); }
  };

  const [resumingPayment, setResumingPayment] = useState(null);
  const handleResumePayment = async (order) => {
    setResumingPayment(order.id);
    try {
      const isRecurring = order.payment_type === "subscription" || order.payment_type === "royal_90";
      const endpoint = isRecurring ? "/payments/subscription-checkout" : "/payments/checkout";
      const { executeCaptcha } = await import("@/lib/recaptcha");
      const captcha_token = await executeCaptcha("checkout").catch(() => null);
      const { data: checkout } = await API.post(endpoint, {
        medication_id: order.medication_id,
        order_id: order.id,
        origin_url: window.location.origin,
        payment_type: order.payment_type || "one_time",
        coupon_id: null,
        captcha_token,
        captcha_version: "v3",
      });
      if (checkout.url) {
        window.location.href = checkout.url;
      } else {
        toast.error("Couldn't start checkout — please try again.");
      }
    } catch (e) {
      const intuitBranch = intuitCtxFromCheckoutError(e, {
        medication_id: order.medication_id,
        payment_type: order.payment_type || "one_time",
        order_id: order.id,
        amount: order.amount,
      });
      if (intuitBranch) { setIntuitCtx(intuitBranch); return; }
      toast.error(e.response?.data?.detail || "Failed to start checkout");
    } finally {
      setResumingPayment(null);
    }
  };

  const approvedOrders = useMemo(() => orders.filter(o => ["approved", "shipped", "delivered"].includes(o.status)), [orders]);
  const pendingOrders = useMemo(() => orders.filter(o => ["pending_review", "pending_payment"].includes(o.status)), [orders]);
  const otherOrders = useMemo(() => orders.filter(o => ["denied", "cancelled"].includes(o.status)), [orders]);
  const hasPaidOrder = useMemo(() => orders.some(o => (o.payment_status === "paid" || o.payment_status === "subscription_active" || o.payment_status === "comp")), [orders]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-lg sm:text-xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <Button variant="ghost" size="sm" className="text-slate-600 rounded-full px-2 sm:px-3" onClick={() => navigate("/profile")} data-testid="profile-btn"><User className="w-4 h-4" /><span className="hidden sm:inline ml-1">Profile</span></Button>
            <span className="text-sm text-slate-600 hidden md:block">{user?.name}</span>
            <Button variant="ghost" size="sm" className="text-slate-500 rounded-full px-2 sm:px-3" onClick={handleLogout} data-testid="dashboard-logout-btn"><LogOut className="w-4 h-4" /><span className="hidden sm:inline ml-1">Logout</span></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12 safe-bottom">
        <HIPAAPatientBanner />
        <PastDueBanner />
        <PatientHelpTour />
        {/* Welcome */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Welcome back, {user?.name?.split(" ")[0]}</h1>
            {topBadge && (
              <Link
                to="/stepathon/rewards"
                data-testid="dashboard-top-badge"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 border border-purple-200 text-purple-800 text-xs font-bold tracking-wide hover:bg-purple-100 transition-colors dark:bg-purple-950/40 dark:border-purple-800/60 dark:text-purple-200"
                title={`Highest badge: ${topBadge.label}`}
              >
                <span className="text-sm">{topBadge.emoji}</span>
                {topBadge.label}
              </Link>
            )}
            {/* Mirrors the mobile home-tile symptom streak badge so
                patients using the site also see the milestone rolling. */}
            <SymptomStreakBadge />
            {streakBell?.sent && (
              <Link
                to="/companion"
                data-testid="dashboard-streak-bell"
                title={`Streak-save reminder fired at ${streakBell.sent_at || "6pm"} — log your weight tonight to keep your ${streakBell.streak || ""}-day streak alive.`}
                className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-xs font-bold tracking-wide hover:bg-amber-100 transition-colors dark:bg-amber-950/40 dark:border-amber-700/60 dark:text-amber-200 animate-pulse"
              >
                🔔 Save your streak
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-rose-500 rounded-full ring-2 ring-white dark:ring-slate-900"></span>
              </Link>
            )}
            {user?.patient_display_id && (
              <span
                data-testid="patient-display-id-chip"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 border border-purple-200 text-purple-800 text-xs font-semibold tracking-wide"
                title="Your GLPFlo Patient ID — appears on every prescription and receipt. Quote to support."
              >
                <span className="opacity-70 font-medium">Patient ID</span>
                <span className="font-mono font-bold">{user.patient_display_id}</span>
              </span>
            )}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/questionnaire/blank.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="print-questionnaire-link"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-100 transition-colors"
              title="Download a printable copy of every onboarding question"
            >
              <FileText className="w-3.5 h-3.5" /> Print onboarding Q's
            </a>
            {user?.account_number && (
              <span
                data-testid="patient-account-chip"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold tracking-wide"
              >
                <span className="opacity-70 font-medium">Account</span>
                <span className="font-mono">{user.account_number}</span>
                {user?.created_at && (
                  <span className="opacity-60 font-medium">· since {new Date(user.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</span>
                )}
                <PatientHelpTooltip
                  testId="account-number"
                  size={12}
                  title="What's my account number?"
                  body={
                    <>
                      <p>This is your unique <b>GLPFlo account number</b>. We use it to find your records quickly when you call or chat with support.</p>
                      <p>Keep it handy — it appears on your receipts and confirmation emails too.</p>
                      <p className="text-[11px] opacity-70 italic">It&apos;s a reference number only. We&apos;ll still verify your identity (DOB + one other detail) before sharing health info.</p>
                    </>
                  }
                />
              </span>
            )}
          </div>
          <p className="text-slate-600">Manage your medications, orders, and health journey. <ReplayTourLink /></p>
        </div>

        {restoreOffer?.offer_available && (
          <div
            data-testid="streak-restore-banner"
            className="mb-6 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-white to-amber-50 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-md"
          >
            <div className="text-3xl">🩹</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">Restore your streak?</p>
              <p className="text-xs text-amber-800/90 mt-0.5">
                We saw you lost a <b>{restoreOffer.broken_from}-day</b> streak — real life happens.
                One-time offer, expires in <b>{restoreOffer.hours_until_expires}h</b>.
              </p>
            </div>
            <Button
              onClick={doStreakRestore}
              data-testid="streak-restore-btn"
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold whitespace-nowrap"
            >
              🔥 Restore my {restoreOffer.broken_from}-day streak
            </Button>
          </div>
        )}

        {/* Yearly savings hero — only renders if patient has paid orders this year */}
        <SavingsWidget user={user} />

        {/* What's included in delivery — global view, only after at least one paid order */}
        {hasPaidOrder && (
          <div className="mb-4">
            <DeliveryIncludesCard />
          </div>
        )}

        {/* Symptom check-in — available to ANY logged-in patient, even
            without an active subscription. Lets people between cycles
            still report side-effects to the clinical team. */}
        <div className="mb-4" data-testid="patient-symptom-card-slot">
          <PatientSymptomCard />
        </div>

        {/* Identity verification (ID + video attestation). Renders inline
            until a clinician verifies; collapses to a small green pill
            after. Required before first eRx, so we keep it near the top. */}
        <IdVerificationCard />

        {/* COA (Certificate of Analysis) lookup — public batch-test reports.
            Lives below ID verification because patients reach for this once
            their vial arrives. Same component the public /coa page uses. */}
        <div className="mb-4">
          <COASearchCard embedded />
        </div>

        {/* Wellness tracker — weekly weigh-in, streak, side-effect log */}
        <div className="mb-6" data-testid="wellness-card-slot">
          <WellnessCard />
        </div>

        {/* Companion App launcher — quick access to the full mobile-style
            tracking suite (food + barcode, exercise, weigh-ins, injections,
            measurements, rewards). Restored after users pointed out the
            web dashboard was missing everything the app can do. */}
        <div className="mb-6" data-testid="companion-launcher-card">
          <div className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-purple-950/40 dark:via-slate-900 dark:to-pink-950/40 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-bold text-purple-600">Wellness Companion</p>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Log meals, workouts, and weigh-ins
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Barcode scanner, calorie & macro tracking, injection log, and body measurements.</p>
              </div>
              <Link
                to="/companion"
                className="hidden sm:inline-flex items-center gap-1 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-4 py-2 whitespace-nowrap"
                data-testid="companion-launcher-open"
              >
                Open <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Link
                to="/companion/food"
                className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-purple-300 p-3 text-center transition-colors"
                data-testid="companion-launcher-food"
              >
                <Utensils className="w-5 h-5 mx-auto text-amber-500" />
                <p className="text-[11px] font-bold text-slate-800 dark:text-slate-100 mt-1.5">Food</p>
                <p className="text-[10px] text-slate-400 mt-0.5 inline-flex items-center gap-0.5"><ScanLine className="w-2.5 h-2.5" />Barcode</p>
              </Link>
              <Link
                to="/companion/exercise"
                className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-emerald-300 p-3 text-center transition-colors"
                data-testid="companion-launcher-exercise"
              >
                <Dumbbell className="w-5 h-5 mx-auto text-emerald-500" />
                <p className="text-[11px] font-bold text-slate-800 dark:text-slate-100 mt-1.5">Move</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Auto kcal</p>
              </Link>
              <Link
                to="/companion/wellness"
                className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-purple-300 p-3 text-center transition-colors"
                data-testid="companion-launcher-body"
              >
                <Activity className="w-5 h-5 mx-auto text-purple-500" />
                <p className="text-[11px] font-bold text-slate-800 dark:text-slate-100 mt-1.5">Weigh-in</p>
                <p className="text-[10px] text-slate-400 mt-0.5">+ injection</p>
              </Link>
              <Link
                to="/companion/rewards"
                className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-pink-300 p-3 text-center transition-colors"
                data-testid="companion-launcher-rewards"
              >
                <Gift className="w-5 h-5 mx-auto text-pink-500" />
                <p className="text-[11px] font-bold text-slate-800 dark:text-slate-100 mt-1.5">Rewards</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Points store</p>
              </Link>
              <Link
                to="/companion"
                className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white p-3 text-center transition-colors sm:hidden col-span-2"
                data-testid="companion-launcher-open-mobile"
              >
                <p className="text-xs font-bold inline-flex items-center gap-1 justify-center">Open Companion <ChevronRight className="w-3.5 h-3.5" /></p>
              </Link>
              <Link
                to="/companion"
                className="hidden sm:flex rounded-xl bg-purple-600 hover:bg-purple-700 text-white p-3 text-center transition-colors items-center justify-center"
                data-testid="companion-launcher-home-btn"
              >
                <p className="text-[11px] font-bold inline-flex items-center gap-1 justify-center">Home <ChevronRight className="w-3.5 h-3.5" /></p>
              </Link>
            </div>
          </div>
        </div>

        {/* AI Wellness Coach — non-medical lifestyle Q&A */}
        <div className="mb-6" data-testid="wellness-coach-slot">
          <WellnessCoachCard />
        </div>

        {/* "What will I pay this month?" — top-level billing preview */}
        <div className="mb-6">
          <BillingPreviewCard />
        </div>

        {/* Education rail — pulls the curated GLPFlo YouTube library. Sits
            between Billing and Subscriptions so it greets returning patients
            with fresh content right after they confirm the month's charge. */}
        <div className="mb-6" data-testid="patient-dashboard-youtube">
          <YouTubeLibraryCard />
        </div>

        {/* Subscription self-management — only renders if patient has any active subscriptions */}
        <SubscriptionsCard />

        {/* Delivery-window exception requests (traveling, etc.) */}
        <div className="mb-6">
          <DeliveryExceptionCard />
        </div>

        {/* Onboarding Banner */}
        {onboarding && !onboarding.is_complete && (
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-6 mb-8 text-white shadow-lg" data-testid="onboarding-banner">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Complete Your Setup</h3>
                <p className="text-purple-200 text-sm">{onboarding.completed} of {onboarding.total} steps done &middot; {onboarding.progress}% complete</p>
                <div className="w-full bg-purple-800 rounded-full h-2 mt-3 max-w-[300px]">
                  <div className="bg-white rounded-full h-2 transition-all" style={{ width: `${onboarding.progress}%` }} />
                </div>
              </div>
              <Button className="rounded-full bg-white text-purple-700 hover:bg-purple-50 px-6 flex-shrink-0" onClick={() => navigate("/onboarding")} data-testid="continue-onboarding-btn">
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-10">
          {[
            questionnaireDone
              ? { to: "/questionnaire", icon: CheckCircle2, label: "Questionnaire", sub: "Completed ✓", bg: "bg-slate-200 dark:bg-slate-700 border border-slate-300 text-slate-600 dark:text-slate-300 cursor-not-allowed", iconClass: "text-emerald-600", subClass: "text-emerald-700 font-semibold", disabled: true }
              : { to: "/questionnaire", icon: FileText, label: "Questionnaire", sub: "Intake form", bg: "bg-purple-600 text-white hover:bg-purple-700", iconClass: "opacity-80", subClass: "text-purple-200" },
            idGateBlocking
              ? { to: "/medications", icon: ShieldAlert, label: "Verify ID & Order", sub: "ID + video required first", bg: "bg-amber-50 border-2 border-amber-300 ring-2 ring-amber-200/60 hover:ring-amber-300", iconClass: "text-amber-600", subClass: "text-amber-700 font-semibold" }
              : { to: "/medications", icon: Package, label: "Order Medication", sub: "Browse & order", bg: "bg-white border border-purple-100", iconClass: "text-purple-600", subClass: "text-slate-400" },
            { to: "/documents", icon: Upload, label: "Documents", sub: "Lab results & ID", bg: "bg-white border border-emerald-100", iconClass: "text-emerald-500", subClass: "text-slate-400" },
            { to: "/profile", icon: User, label: "Profile", sub: "Demographics", bg: "bg-white border border-blue-100", iconClass: "text-blue-500", subClass: "text-slate-400" },
          ].map((a) => (
            <button
              key={a.to + a.label}
              onClick={() => { if (!a.disabled) navigate(a.to); }}
              disabled={a.disabled}
              className={`${a.bg} rounded-2xl p-5 text-left ${a.disabled ? "opacity-70" : "hover:shadow-lg hover:-translate-y-1"} transition-all duration-300 shadow-[0_4px_20px_rgb(0,0,0,0.03)]`}
              data-testid={`quick-${a.label.toLowerCase().replace(/ /g, "-")}${a.disabled ? "-done" : ""}`}
            >
              <a.icon className={`w-6 h-6 mb-2 ${a.iconClass}`} />
              <p className="font-semibold text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{a.label}</p>
              <p className={`text-xs mt-0.5 ${a.subClass}`}>{a.sub}</p>
            </button>
          ))}
          {/* Contact GLPFlo — primary customer care + secondary business line, no email */}
          {contactInfo.phone_number && (
            <a href={`tel:${contactInfo.phone_number}`} className="bg-white border border-slate-200 rounded-2xl p-5 text-left hover:shadow-lg hover:-translate-y-1 transition-all duration-300 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="call-glpflo-btn">
              <Phone className="w-6 h-6 mb-2 text-purple-500" />
              <p className="font-semibold text-sm text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Call Us</p>
              <p className="text-xs text-slate-500 mt-0.5">{contactInfo.phone_number}</p>
              {contactInfo.secondary_phone_number && (
                <p className="text-[11px] text-slate-400 mt-0.5" data-testid="call-glpflo-secondary">or {contactInfo.secondary_phone_number}</p>
              )}
            </a>
          )}
          {!contactInfo.phone_number && (
            <button onClick={() => navigate("/medications")} className="bg-white border border-amber-100 rounded-2xl p-5 text-left hover:shadow-lg hover:-translate-y-1 transition-all duration-300 shadow-[0_4px_20px_rgb(0,0,0,0.03)] col-span-2" data-testid="new-order-btn">
              <Plus className="w-6 h-6 mb-2 text-amber-500" />
              <p className="font-semibold text-sm text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>New Order</p>
              <p className="text-xs text-slate-400 mt-0.5">Request medication</p>
            </button>
          )}
        </div>

        {/* Referral Card */}
        <div className="mb-10" data-testid="referral-section">
          <ReferralCard compact />
        </div>

        {/* Active Medications / Approved Orders */}
        {approvedOrders.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Active Medications</h2>            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="active-medications">
              {approvedOrders.map((order) => (
                <div key={order.id} className="bg-white rounded-2xl border border-emerald-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`active-med-${order.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle className="w-3 h-3" /> Approved
                    </span>
                    {order.payment_type === "subscription" && (() => {
                      const SUB_BADGE = {
                        paused: { cls: "bg-amber-50 text-amber-700", label: "Paused" },
                        cancelled: { cls: "bg-red-50 text-red-700", label: "Cancelled" },
                      };
                      const badge = SUB_BADGE[order.subscription_status] || { cls: "bg-purple-50 text-purple-700", label: "Subscription" };
                      return (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${badge.cls}`}>
                          {badge.label}
                        </span>
                      );
                    })()}
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{order.medication_name}</h3>
                  <p className="text-sm text-slate-500 mb-1">{order.medication_type}</p>
                  {(pharmacyMap[order.pharmacy_tenant_id] || defaultPharmacy) && (
                    <p className="text-xs text-purple-700 mb-1 flex items-center gap-1" data-testid={`active-med-pharmacy-${order.id}`}>
                      <Pill className="w-3 h-3" />
                      Compounded by <span className="font-medium">{pharmacyMap[order.pharmacy_tenant_id] || defaultPharmacy}</span>
                    </p>
                  )}
                  {order.erx_id && <p className="text-xs text-emerald-600 font-mono mb-1">ERX: {order.erx_id}</p>}
                  {!order.tracking_number && order.status === "approved" && (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200"
                      data-testid={`shipping-eta-${order.id}`}
                      title="Estimated arrival from approval — actual delivery times may vary by carrier and location"
                    >
                      <Package className="w-3 h-3" />
                      Tracking ETA: 2–4 business days from approval
                    </div>
                  )}
                  {order.tracking_number && (
                    <div className="mt-2 p-2 rounded-lg bg-indigo-50 border border-indigo-100" data-testid={`card-tracking-${order.id}`}>
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <div className="min-w-0 flex-1">
                          <p className="text-indigo-700 font-medium">{order.status === "delivered" ? "Delivered" : "Shipped"} · {order.carrier || order.tracking_carrier || "—"}</p>
                          <p className="font-mono text-slate-700 truncate">{order.tracking_number}</p>
                        </div>
                        {order.tracking_url && (
                          <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                            data-testid={`card-track-btn-${order.id}`}
                            className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-2 py-1 font-medium flex-shrink-0">
                            <Package className="w-3 h-3" /> Track
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Warm-vial reassurance link — only surfaced once a package
                      is in transit. This is exactly the moment patients start
                      to worry about cold-chain & call support. */}
                  {(order.status === "shipped" || order.status === "delivered") && (
                    <Link
                      to="/shipping-study"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors group"
                      data-testid={`warm-vial-link-${order.id}`}
                    >
                      <span className="text-base shrink-0" aria-hidden>🌡️</span>
                      <span className="text-xs flex-1 min-w-0">
                        <span className="font-semibold text-amber-800">Vial just arrived warm?</span>{" "}
                        <span className="text-amber-700">Read this — it&apos;s normal.</span>
                      </span>
                      <ChevronRight className="w-3.5 h-3.5 text-amber-600 group-hover:translate-x-0.5 transition-transform shrink-0" />
                    </Link>
                  )}
                  {/* Vial COA badge — surfaces the auto-linked Certificate
                      of Analysis for the exact lot the patient received.
                      Auto-linked at LabFlo ship time (see services/coa_linker.py). */}
                  {order.coa_id && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/coa/${order.coa_id}/file`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors group"
                      data-testid={`order-coa-link-${order.id}`}
                    >
                      <FileText className="w-4 h-4 text-emerald-700 shrink-0" />
                      <span className="text-xs flex-1 min-w-0">
                        <span className="font-semibold text-emerald-800">Verify your vial</span>{" "}
                        <span className="text-emerald-700">Lot {order.coa_lot_number} · Certificate of Analysis</span>
                      </span>
                      <ChevronRight className="w-3.5 h-3.5 text-emerald-600 group-hover:translate-x-0.5 transition-transform shrink-0" />
                    </a>
                  )}
                  {order.coa_status === "pending" && order.lot_number && (
                    <div
                      className="mt-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600"
                      data-testid={`order-coa-pending-${order.id}`}
                    >
                      <FileText className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                      Certificate of Analysis for lot{" "}
                      <span className="font-mono text-slate-700">{order.lot_number}</span> will appear here once the pharmacy uploads it.
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white flex-1" onClick={() => setRefillModal(order)} data-testid={`refill-${order.id}`}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Request Refill
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => viewOrderDetails(order)} data-testid={`view-details-${order.id}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    {receiptsByOrder[order.id] && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => openReceiptPreview(order.id)}
                        title="View payment receipt"
                        data-testid={`receipt-${order.id}`}
                      >
                        <Receipt className="w-3 h-3" />
                      </Button>
                    )}
                    {order.payment_type === "subscription" && order.subscription_status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSubAction(order)} data-testid={`manage-sub-${order.id}`}>
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Orders */}
        {pendingOrders.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 inline-flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Pending Orders
              <PatientHelpTooltip
                testId="pending-orders"
                title="What are pending orders?"
                body={
                  <>
                    <p>Orders waiting on one of three things:</p>
                    <p><b className="text-amber-700">Pending Review</b> — a clinician is verifying your medication is safe and appropriate. Usually 24-48 hours.</p>
                    <p><b className="text-blue-700">Pending Payment</b> — clinician approved; tap <b>Pay & Order</b> to complete checkout.</p>
                    <p><b className="text-emerald-700">Approved</b> — payment received and your prescription has been sent to our pharmacy partner for fulfillment.</p>
                    <p className="text-[11px] opacity-70">You&apos;ll get an email at each step. If anything looks stuck, message support — we&apos;ll check.</p>
                  </>
                }
              />
            </h2>
            <div className="space-y-3" data-testid="pending-orders">
              {pendingOrders.map((order) => {
                const st = STATUS_MAP[order.status] || STATUS_MAP.pending_review;
                const StIcon = st.icon;
                return (
                  <div key={order.id} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between gap-4" data-testid={`pending-${order.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><StIcon className="w-5 h-5 text-amber-500" /></div>
                      <div>
                        <p className="font-medium text-slate-900">{order.medication_name}</p>
                        <p className="text-xs text-slate-400">Order #{order.id?.slice(0, 8)} &middot; {new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${st.color}`}>{st.label}</span>
                      {order.status === "pending_payment" && (
                        <Button
                          size="sm"
                          className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => handleResumePayment(order)}
                          disabled={resumingPayment === order.id}
                          data-testid={`pay-now-${order.id}`}
                        >
                          {resumingPayment === order.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CreditCard className="w-3 h-3 mr-1" />}
                          Pay Now
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full text-slate-500 hover:text-red-600 hover:border-red-200" onClick={() => viewOrderDetails(order)} data-testid={`view-pending-${order.id}`}>
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleCancelOrder(order.id)} data-testid={`cancel-order-${order.id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Full Order History */}
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4 inline-flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Order History
            <PatientHelpTooltip
              testId="receipts"
              title="Where are my receipts?"
              body={
                <>
                  <p>Tap the <b>Receipt</b> button on any past order to view or download a PDF receipt for that purchase.</p>
                  <p>The receipt includes your <b>account number</b>, the date, the medication and dose, and the amount charged — perfect for HSA / FSA reimbursement.</p>
                  <p>We also email you a copy automatically on every successful payment, so check your inbox if you can&apos;t find it here.</p>
                </>
              }
            />
            <button
              type="button"
              onClick={() => openBenefitsShareModal()}
              className="ml-2 text-xs font-medium text-purple-600 hover:text-purple-700 underline-offset-2 hover:underline inline-flex items-center gap-1"
              data-testid="order-history-submit-hsa-link"
            >
              Submit to HSA / FSA →
            </button>
          </h2>
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-purple-100 p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="no-orders-message">
              <Package className="w-12 h-12 text-purple-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>No Orders Yet</h3>
              <p className="text-slate-500 mb-6">Complete your questionnaire and browse medications to get started.</p>
              <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-6" onClick={() => navigate("/questionnaire")} data-testid="empty-state-questionnaire-btn">
                Start Questionnaire <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="orders-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-slate-600 font-medium">Medication</th>
                    <th className="text-left px-4 py-3 text-slate-600 font-medium hidden sm:table-cell">Type</th>
                    <th className="text-left px-4 py-3 text-slate-600 font-medium hidden md:table-cell">Date</th>
                    <th className="text-left px-4 py-3 text-slate-600 font-medium">Status</th>
                    <th className="text-left px-4 py-3 text-slate-600 font-medium hidden md:table-cell">Payment</th>
                    <th className="text-left px-4 py-3 text-slate-600 font-medium hidden lg:table-cell">ERX</th>
                    <th className="text-right px-4 py-3 text-slate-600 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.map((order) => {
                    const st = STATUS_MAP[order.status] || STATUS_MAP.pending_review;
                    return (
                      <tr key={order.id} className="hover:bg-slate-50" data-testid={`order-row-${order.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{order.medication_name}</p>
                          {order.is_refill && <span className="text-[10px] text-purple-600 font-medium">REFILL</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{order.medication_type}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs hidden md:table-cell">{new Date(order.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3"><span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full border ${st.color}`}>{st.label}</span></td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${order.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{order.payment_status || "N/A"}</span>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-emerald-600 hidden lg:table-cell">{order.erx_id || "-"}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1">
                            {receiptsByOrder[order.id] && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openReceiptPreview(order.id)}
                                title="View receipt"
                                data-testid={`receipt-row-${order.id}`}
                              >
                                <Receipt className="w-4 h-4 text-purple-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => viewOrderDetails(order)} data-testid={`view-order-${order.id}`}><Eye className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Contact Section */}
        {(contactInfo.phone_number || contactInfo.secondary_phone_number) && (
          <div className="mt-10 bg-purple-50 rounded-2xl border border-purple-100 p-6" data-testid="contact-section">
            <h3 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Need Help? Contact the GLPFlo Prescribing Team</h3>
            <div className="flex flex-wrap gap-4">
              {contactInfo.phone_number && (
                <a href={`tel:${contactInfo.phone_number}`} className="inline-flex items-center gap-2 bg-white rounded-full px-5 py-2.5 border border-purple-200 text-purple-700 hover:bg-purple-100 transition-colors" data-testid="contact-phone">
                  <Phone className="w-4 h-4" /> {contactInfo.phone_number}
                </a>
              )}
              {contactInfo.secondary_phone_number && (
                <a href={`tel:${contactInfo.secondary_phone_number}`} className="inline-flex items-center gap-2 bg-white rounded-full px-5 py-2.5 border border-purple-200 text-purple-700 hover:bg-purple-100 transition-colors text-sm" data-testid="contact-phone-secondary">
                  <Phone className="w-4 h-4" /> {contactInfo.secondary_phone_number}
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      <Dialog open={!!selectedOrder} onOpenChange={() => { setSelectedOrder(null); setOrderDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Order Details</DialogTitle></DialogHeader>
          {detailLoading ? <div className="py-8 text-center text-slate-400">Loading...</div> : orderDetail && (
            <div className="space-y-4" data-testid="order-detail-modal">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-slate-500">Medication</p><p className="font-medium">{orderDetail.order.medication_name}</p></div>
                <div><p className="text-xs text-slate-500">Type</p><p className="font-medium">{orderDetail.order.medication_type}</p></div>
                <div><p className="text-xs text-slate-500">Status</p><p className="font-medium capitalize">{orderDetail.order.status?.replace(/_/g, " ")}</p></div>
                <div><p className="text-xs text-slate-500">Payment</p><p className="font-medium capitalize">{orderDetail.order.payment_status} ({orderDetail.order.payment_type})</p></div>
                <div><p className="text-xs text-slate-500">Created</p><p className="font-medium">{new Date(orderDetail.order.created_at).toLocaleString()}</p></div>
                {orderDetail.order.erx_id && <div><p className="text-xs text-slate-500">ERX ID</p><p className="font-medium text-emerald-600 font-mono">{orderDetail.order.erx_id}</p></div>}
              </div>
              {orderDetail.order.prescriber_notes && (
                <div className="bg-slate-50 rounded-xl p-4 border"><p className="text-xs text-slate-500 mb-1">Provider Notes</p><p className="text-sm">{orderDetail.order.prescriber_notes}</p></div>
              )}
              {receiptsByOrder[orderDetail.order.id] && (
                <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 flex items-center gap-3" data-testid="modal-receipt-block">
                  <div className="w-10 h-10 rounded-xl bg-white border border-purple-100 flex items-center justify-center shrink-0">
                    <Receipt className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">Payment receipt available</p>
                    <p className="text-xs text-slate-500">
                      Receipt <span className="font-mono">{receiptsByOrder[orderDetail.order.id].receipt_id}</span> · {receiptsByOrder[orderDetail.order.id].tier_label}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full shrink-0"
                    onClick={() => openReceiptPreview(orderDetail.order.id)}
                    data-testid="modal-download-receipt-btn"
                  >
                    <Receipt className="w-3.5 h-3.5 mr-1.5" />
                    View receipt
                  </Button>
                </div>
              )}
              {orderDetail.erx && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Prescription Details</h4>
                  <div className="grid grid-cols-2 gap-3 bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                    <div><p className="text-xs text-emerald-700">Medication</p><p className="font-medium text-sm">{orderDetail.erx.medication_name}</p></div>
                    <div><p className="text-xs text-emerald-700">Dosage</p><p className="font-medium text-sm">{orderDetail.erx.dosage || "-"}</p></div>
                    <div><p className="text-xs text-emerald-700">Quantity</p><p className="font-medium text-sm">{orderDetail.erx.quantity || "-"}</p></div>
                    <div><p className="text-xs text-emerald-700">Refills</p><p className="font-medium text-sm">{orderDetail.erx.refills || "0"}</p></div>
                    <div className="col-span-2"><p className="text-xs text-emerald-700">Directions</p><p className="font-medium text-sm">{orderDetail.erx.directions || "-"}</p></div>
                    <div><p className="text-xs text-emerald-700">Prescriber</p><p className="font-medium text-sm">{orderDetail.erx.prescriber_name}</p></div>
                    {orderDetail.erx.np_statement && <div className="col-span-2"><p className="text-xs text-amber-700 italic">{orderDetail.erx.np_statement}</p></div>}
                  </div>
                </div>
              )}
              {orderDetail.pharmacy && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Pharmacy</h4>
                  <p className="text-sm text-slate-700">{orderDetail.pharmacy.name}</p>
                  <p className="text-xs text-slate-500">{orderDetail.pharmacy.location} &middot; {orderDetail.pharmacy.type}</p>
                </div>
              )}
              {/* What's in this delivery — per-order view */}
              {(orderDetail.order.payment_status === "paid" || orderDetail.order.payment_status === "subscription_active" || orderDetail.order.payment_status === "comp") && (
                <div className="border-t pt-4">
                  <DeliveryIncludesCard orderId={orderDetail.order.id} />
                </div>
              )}
              {/* Order Tracking */}
              {(orderDetail.order.tracking_number || orderDetail.order.shipped_at) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Shipping & Tracking</h4>
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 space-y-2">
                    {orderDetail.order.tracking_number && (
                      <div><p className="text-xs text-indigo-700">Tracking Number</p><p className="font-medium text-sm font-mono" data-testid="patient-tracking-number">{orderDetail.order.tracking_number}</p></div>
                    )}
                    {(orderDetail.order.carrier || orderDetail.order.tracking_carrier) && (
                      <div><p className="text-xs text-indigo-700">Carrier</p><p className="font-medium text-sm">{orderDetail.order.carrier || orderDetail.order.tracking_carrier}</p></div>
                    )}
                    {orderDetail.order.shipped_at && (
                      <div><p className="text-xs text-indigo-700">Shipped</p><p className="font-medium text-sm">{new Date(orderDetail.order.shipped_at).toLocaleString()}</p></div>
                    )}
                    {orderDetail.order.delivered_at && (
                      <div><p className="text-xs text-indigo-700">Delivered</p><p className="font-medium text-sm">{new Date(orderDetail.order.delivered_at).toLocaleString()}</p></div>
                    )}
                    {orderDetail.order.tracking_url && (
                      <a href={orderDetail.order.tracking_url} target="_blank" rel="noopener noreferrer"
                        data-testid="patient-track-package-btn"
                        className="mt-2 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg px-4 py-2 transition-colors">
                        <Package className="w-4 h-4" /> Track Package
                      </a>
                    )}
                  </div>
                </div>
              )}
              {/* Status Flow */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Order Status</h4>
                <div className="flex items-center gap-1">
                  {["pending_payment","pending_review","approved","shipped","delivered"].map((s, i) => {
                    const statusOrder = ["pending_payment","pending_review","approved","shipped","delivered"];
                    const currentIdx = statusOrder.indexOf(orderDetail.order.status);
                    const isComplete = i <= currentIdx && !["cancelled","denied"].includes(orderDetail.order.status);
                    const isCurrent = s === orderDetail.order.status;
                    return (
                      <div key={s} className="flex items-center gap-1 flex-1">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${dotClass(isComplete, isCurrent)}`} />
                        <span className={`text-[10px] ${isComplete ? "text-emerald-700 font-medium" : "text-slate-400"} hidden sm:block`}>{s.replace(/_/g," ").replace(/\b\w/g,l=>l.toUpperCase())}</span>
                        {i < 4 && <div className={`h-0.5 flex-1 ${isComplete ? "bg-emerald-300" : "bg-slate-200"}`} />}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Cancel button for cancellable orders */}
              {["pending_payment","pending_review","approved"].includes(orderDetail.order.status) && (
                <div className="border-t pt-4 flex flex-wrap gap-2">
                  {orderDetail.order.status === "pending_payment" && (
                    <Button
                      className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => handleResumePayment(orderDetail.order)}
                      disabled={resumingPayment === orderDetail.order.id}
                      data-testid="modal-pay-now-btn"
                    >
                      {resumingPayment === orderDetail.order.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CreditCard className="w-4 h-4 mr-1" />}
                      Pay Now
                    </Button>
                  )}
                  <Button variant="outline" className="rounded-full text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleCancelOrder(orderDetail.order.id)} data-testid="modal-cancel-order-btn">
                    <XCircle className="w-4 h-4 mr-1" /> Cancel This Order
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refill Confirmation Modal */}
      <Dialog open={!!refillModal} onOpenChange={() => setRefillModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Request Refill</DialogTitle></DialogHeader>
          {refillModal && (
            <div className="space-y-4" data-testid="refill-modal">
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <p className="font-semibold text-slate-900">{refillModal.medication_name}</p>
                <p className="text-sm text-slate-600">{refillModal.medication_type}</p>
                <p className="text-xs text-slate-400 mt-1">Original order: #{refillModal.id?.slice(0, 8)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Note to Prescriber (optional)</label>
                <textarea value={refillNote} onChange={(e) => setRefillNote(e.target.value)} placeholder="Any changes, concerns, or updates for your prescriber..." className="w-full rounded-xl border border-slate-200 p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-purple-500 focus:border-purple-500" data-testid="refill-patient-note" />
                <p className="text-xs text-slate-400 mt-1">Your prescriber must read and acknowledge this note before processing your order.</p>
              </div>
              <p className="text-sm text-slate-600">This will create a new order for the same medication. You&apos;ll be directed to payment, then a prescriber will review and approve your refill.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefillModal(null)}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => handleRefill(refillModal)} disabled={refillLoading} data-testid="confirm-refill-btn">
              {refillLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Confirm Refill</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Management Modal */}
      <Dialog open={!!subAction} onOpenChange={() => setSubAction(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Manage Subscription</DialogTitle></DialogHeader>
          {subAction && (
            <div className="space-y-4" data-testid="subscription-modal">
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <p className="font-semibold text-slate-900">{subAction.medication_name}</p>
                <p className="text-sm text-slate-600">Subscription</p>
                <p className="text-xs text-slate-400">Status: {subAction.subscription_status || "active"}</p>
              </div>
              <div className="space-y-2">
                {subAction.subscription_status !== "paused" && (
                  <Button className="w-full justify-start rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200" onClick={() => handleSubAction(subAction.id, "pause")} data-testid="pause-sub-btn">
                    <Pause className="w-4 h-4 mr-3" /> Pause Subscription
                  </Button>
                )}
                {subAction.subscription_status === "paused" && (
                  <Button className="w-full justify-start rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200" onClick={() => handleSubAction(subAction.id, "resume")} data-testid="resume-sub-btn">
                    <Play className="w-4 h-4 mr-3" /> Resume Subscription
                  </Button>
                )}
                <Button className="w-full justify-start rounded-xl bg-red-50 hover:bg-red-100 text-red-700 border border-red-200" onClick={() => handleSubAction(subAction.id, "cancel")} data-testid="cancel-sub-btn">
                  <Ban className="w-4 h-4 mr-3" /> Cancel Subscription
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Chat Widget is now mounted globally in App.js — no per-page mount. */}

      <ReceiptPreviewDialog
        open={!!previewReceipt}
        onClose={() => setPreviewReceipt(null)}
        sessionId={previewReceipt?.session_id}
        receiptId={previewReceipt?.receipt_id}
        tierLabel={previewReceipt?.tier_label}
      />

      {/* Mounted once at dashboard level — opens via openBenefitsShareModal() from
          BillingPreviewCard's action button AND from the Order History sublink. */}
      <BenefitsShareModal />

      <IntuitCheckoutModal
        open={!!intuitCtx}
        onOpenChange={(open) => { if (!open) setIntuitCtx(null); }}
        ctx={intuitCtx}
      />

      <PublicFooter />
    </div>
  );
}


/**
 * Tiny "Replay tour" link that appears next to the welcome subtitle.
 * - Hidden by default (no point showing it during the first-visit run).
 * - Once the tour has been completed/skipped (the storage flag exists),
 *   the link renders so patients can re-watch anytime.
 * - Clicking opens the same `PatientHelpTour` in forced-open mode.
 */
function ReplayTourLink() {
  const [seen, setSeen] = useState(false);
  const [showAgain, setShowAgain] = useState(false);
  useEffect(() => {
    const sync = () => {
      try { setSeen(!!window.localStorage.getItem("glpflo_patient_tour_v1")); } catch { /* noop */ }
    };
    sync();
    // Listen for the just-completed event so the link appears immediately
    // after the user skips/finishes the first-run tour (without reload).
    window.addEventListener("glpflo:tour-seen", sync);
    return () => window.removeEventListener("glpflo:tour-seen", sync);
  }, []);
  if (!seen) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setShowAgain(true)}
        className="ml-1 text-xs text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline transition"
        data-testid="replay-tour-link"
      >
        Replay welcome tour
      </button>
      {showAgain && <PatientHelpTour forceOpen onClose={() => setShowAgain(false)} />}
    </>
  );
}
